import { Button, Icons, Loading, MultiSelect } from '@commenda/kakashi';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { getPrintableDirectorName } from '@commenda/utils';
import { CorporationType, Currency, StakeholderType } from '@prisma/client';
import { useState } from 'react';
import { KeyedMutator } from 'swr';
import { IncorporationFormHeading } from '../../common';
import CorporateStakeholderModal from '../../common/corporateStakeholder/CorporateStakeholderModal';
import { CorporateShareholderEquitySplitDistribution } from '../../common/corporateStakeholder/EquitySplitDistribution';
import StakeholderOptionsModal from '../../common/corporateStakeholder/StakeholderOptionsModal';
import CapTableKS from '../../EquityInfo/CapTable';
import { useEquityFormState } from '../../EquityInfo/hooks/useEquityFormState';
import { useEquityResources } from '../../EquityInfo/hooks/useEquityResources';
import { IndividualDirectorFormModal } from '../components/modals/IndividualDirectorForm';
import { TUAEIncorporationRequestData } from '../types';
import CompanyValuationSection from './CompanyValuation';
import { validateStakeholders } from './validators';

type TEquityProps = {
  workRequest: WorkRequestResponseType & {
    requestData: TUAEIncorporationRequestData;
  };
  next: () => void;
  back: () => void;
  mutate: KeyedMutator<unknown>;
  setNotification: TSetNotification;
};

export const UAEEquity = ({
  next,
  back,
  workRequest,
  mutate,
  setNotification,
}: TEquityProps) => {
  const [paidUpCapital, setPaidUpCapital] = useState<number>(
    workRequest.requestData?.paidUpCapital
  );

  const {
    directorsData,
    corporations,
    capTableTransactions,
    stockClasses,
    mutateDirectors,
    mutateCorporations,
    mutateCapTableTransactions,
    stakeholderOptions,
    isLoading,
    isError,
  } = useEquityResources(workRequest.companyId, workRequest.corporationId);

  const {
    equityInfo,
    setEquityInfo,
    selectedStakeholders,
    setSelectedStakeholders,
    setCorporateShareholderToEdit,
    corporateStakeholderModalVisibility,
    directorModalVisibility,
    stakeholderIdsWithError,
    setStakeholderIdsWithError,
    setCapTableTransactionIdsToDelete,
    isSubmitting,
    stakeholderTypeModalVisibility,
    onAddCorporateShareholder,
    onSubmit,
    selectAddedShareholder,
    onChangeShareholder,
    individualShareholderToEdit,
    corporateShareholderToEdit,
    capTableData,
    onEditShareholderDetails,
  } = useEquityFormState({
    capTableTransactions,
    stockClasses,
    directorsData: directorsData?.directors,
    corporations,
    mutateCorporations,
    resetEditModalPresetState: () => {},
    corporationId: workRequest.corporationId,
    config: {
      defaultShareCount: 1,
      defaultSharePrice: 1,
      currency: Currency.GBP,
    },
    onSubmitSuccess: () => {
      mutate();
      mutateCapTableTransactions();
      next?.();
    },
    validateStakeholdersAndGetIdsWithError: validateStakeholders,
  });

  if (isLoading || isError) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col gap-10">
      <IncorporationFormHeading
        text="Company Ownership"
        additionalText="Please provide information about this corporation's shareholders."
      />
      <CompanyValuationSection
        equityInfo={equityInfo}
        setEquityInfo={setEquityInfo}
        paidUpCapital={paidUpCapital}
        setPaidUpCapital={setPaidUpCapital}
      />

      <div>
        <h3 className="text-base">Shareholder Equity</h3>
      </div>

      <div className=" flex flex-col gap-4 pb-12 border-b">
        <MultiSelect
          defaultValue={Object.keys(selectedStakeholders || {})}
          id="shareholder-multiselect"
          placeholder="Select shareholders"
          options={
            stakeholderOptions
              ?.map((stakeholder) => {
                return {
                  label: stakeholder.name,
                  value: stakeholder.id,
                };
              })
              ?.sort((a, b) => a.label.localeCompare(b.label)) || []
          }
          onValueChange={onChangeShareholder}
          hideSelectAll
          hideTags
          addItemConfig={{
            shouldAllowAdd: true,
            placeholder: 'Add shareholder',
            onAddItem: stakeholderTypeModalVisibility.show,
          }}
        />
        <CorporateShareholderEquitySplitDistribution
          selectedStakeholders={selectedStakeholders}
          setSelectedStakeholders={setSelectedStakeholders}
          equityInfo={equityInfo}
          onChangeShareholder={onChangeShareholder}
          onEditShareholderDetails={onEditShareholderDetails}
          stakeholderIdsWithError={Array.from(stakeholderIdsWithError)}
          setNotification={setNotification}
          setCapTableTransactionIdsToDelete={setCapTableTransactionIdsToDelete}
        />
        <StakeholderOptionsModal
          isShown={stakeholderTypeModalVisibility.visible}
          toggle={stakeholderTypeModalVisibility.hide}
          onShowDirectorModal={directorModalVisibility.show}
          onShowCorporateShareholderModal={() => {
            stakeholderTypeModalVisibility.hide();
            corporateStakeholderModalVisibility.show();
          }}
        />
      </div>

      <div className="flex flex-col pt-2 gap-4">
        {workRequest?.requestData?.corporationType === CorporationType.CCorp ? (
          <h3 className="text-base">Ownership Summary</h3>
        ) : null}
        <CapTableKS
          data={capTableData}
          showEsopPool={false}
          isNumberOfSharesApplicable
        />
      </div>

      <div className="flex gap-4">
        <Button variant="secondary" onClick={back}>
          <Icons.ArrowRight className="rotate-180 mr-2 h-4 w-4" />
          <span>Back</span>
        </Button>
        <Button onClick={onSubmit}>
          <span>Continue</span>
          <Icons.ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {directorModalVisibility.visible ? (
        <IndividualDirectorFormModal
          isOpen={directorModalVisibility.visible}
          onClose={directorModalVisibility.hide}
          workRequest={workRequest}
          setNotification={setNotification}
          director={individualShareholderToEdit}
          onSuccess={async (newDirector) => {
            stakeholderTypeModalVisibility.hide();
            await mutateDirectors();
            selectAddedShareholder({
              type: StakeholderType.INDIVIDUAL,
              id: newDirector.id,
              name: getPrintableDirectorName(newDirector),
            });
            setCorporateShareholderToEdit(undefined);
            setStakeholderIdsWithError((prev) => {
              const newSet = new Set(prev);
              newSet.delete(`INDIVIDUAL_${newDirector.id}`);
              return newSet;
            });
          }}
        />
      ) : null}

      <CorporateStakeholderModal
        selectedCorporateStakeholderId={corporateShareholderToEdit}
        setSelectedCorporateStakeholderId={setCorporateShareholderToEdit}
        companyId={workRequest.companyId}
        isShown={corporateStakeholderModalVisibility.visible}
        toggle={corporateStakeholderModalVisibility.hide}
        postSave={({
          corporationId: corporateStakeholderId,
          corporationName,
        }) =>
          onAddCorporateShareholder({
            corporateStakeholderId,
            corporationName,
          })
        }
        mutate={async () => {
          await mutateCorporations();
        }}
        setNotification={setNotification}
        DirectorForm={null}
        directors={directorsData?.directors}
        mutateDirectors={mutateDirectors}
        corporations={corporations}
        workRequestData={workRequest}
      />
    </div>
  );
};
